import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

const EMPTY = { title:'', meeting_date:'', start_time:'09:00', end_time:'10:00', meeting_room_id:'', host_employee:'', guests:'', description:'' };
const STATUS_COLORS = { scheduled:'bg-blue-100 text-blue-700', ongoing:'bg-green-100 text-green-700', completed:'bg-gray-100 text-gray-600', cancelled:'bg-red-100 text-red-600' };

export default function Meetings() {
  const { user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewDate, setViewDate] = useState(new Date().toISOString().split('T')[0]);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try { const r = await api.get(`/api/meetings?date=${viewDate}`); setMeetings(r.data); }
    catch (err) { console.error('meetings:', err); setMeetings([]); }
    try { const r = await api.get('/api/meeting-rooms/active'); setRooms(r.data); }
    catch (err) { console.error('meeting-rooms:', err); setRooms([]); }
    try { const r = await api.get('/api/employees'); setEmployees(r.data); }
    catch (err) { console.error('employees:', err); setEmployees([]); }
    setLoading(false);
  };

  useEffect(() => { if (user) load(); }, [user?.role, viewDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/api/meetings/${editing}`, form);
      else await api.post('/api/meetings', form);
      setShowForm(false); setForm(EMPTY); setEditing(null); load();
    } catch (err) { alert(err.response?.data?.error || 'Failed to save meeting'); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this meeting?')) return;
    try { await api.delete(`/api/meetings/${id}`); load(); }
    catch { alert('Failed to cancel'); }
  };

  const handleEdit = (m) => {
    setForm({ title:m.title, meeting_date:m.meeting_date?.split('T')[0]||m.meeting_date,
      start_time:m.start_time?.substring(0,5), end_time:m.end_time?.substring(0,5),
      meeting_room_id:m.meeting_room_id||'', host_employee:m.host_employee||'', guests:m.guests||'', description:m.description||'' });
    setEditing(m.id); setShowForm(true);
  };

  // Build time slots for calendar view
  const hours = Array.from({length:12},(_,i)=>i+8);

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Meeting Reservation</h1>
          <p className="text-gray-500 text-sm">{meetings.length} meeting(s) on {viewDate}</p>
        </div>
        <button onClick={() => { setForm({...EMPTY, meeting_date:viewDate}); setEditing(null); setShowForm(true); }}
          className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm font-medium">
          ➕ New Meeting
        </button>
      </div>

      {/* Date picker */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
        <button onClick={() => { const d=new Date(viewDate); d.setDate(d.getDate()-1); setViewDate(d.toISOString().split('T')[0]); }}
          className="p-2 border rounded-lg hover:bg-gray-50">◀</button>
        <input type="date" value={viewDate} onChange={e => setViewDate(e.target.value)}
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-center font-medium" />
        <button onClick={() => { const d=new Date(viewDate); d.setDate(d.getDate()+1); setViewDate(d.toISOString().split('T')[0]); }}
          className="p-2 border rounded-lg hover:bg-gray-50">▶</button>
        <button onClick={() => setViewDate(new Date().toISOString().split('T')[0])}
          className="px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100">Today</button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">{editing ? 'Edit Meeting' : 'New Meeting'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Title *</label>
                <input type="text" value={form.title} onChange={e => setForm({...form,title:e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input type="date" value={form.meeting_date} onChange={e => setForm({...form,meeting_date:e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Room</label>
                <select value={form.meeting_room_id} onChange={e => setForm({...form,meeting_room_id:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— No Room —</option>
                  {rooms.map(r => (
                    <option key={r.id} value={r.id}>
                      {r.name}{r.company_name ? ` — ${r.company_name}` : ''} (Cap: {r.capacity})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time *</label>
                <input type="time" value={form.start_time} onChange={e => setForm({...form,start_time:e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Time *</label>
                <input type="time" value={form.end_time} onChange={e => setForm({...form,end_time:e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Host Employee</label>
                <select value={form.host_employee} onChange={e => setForm({...form,host_employee:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Select —</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Guests (comma separated)</label>
                <input type="text" value={form.guests} onChange={e => setForm({...form,guests:e.target.value})} placeholder="John Doe, Jane Smith..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm({...form,description:e.target.value})} rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="sm:col-span-2 flex gap-3 pt-2">
                <button type="submit" className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium">
                  {editing ? 'Update' : 'Create Meeting'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}
                  className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Meetings List */}
      {loading ? (
        <div className="bg-white rounded-xl p-12 text-center text-gray-400">Loading...</div>
      ) : meetings.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center text-gray-400">
          <div className="text-5xl mb-3">📅</div><p>No meetings scheduled for this date</p>
        </div>
      ) : (
        <div className="space-y-3">
          {meetings.map(m => (
            <div key={m.id} className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md transition-shadow">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-4">
                  <div className="bg-blue-50 rounded-xl p-3 text-center min-w-[80px]">
                    <div className="text-blue-700 font-bold">{m.start_time?.substring(0,5)}</div>
                    <div className="text-gray-400 text-xs">to</div>
                    <div className="text-blue-700 font-bold">{m.end_time?.substring(0,5)}</div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 text-lg">{m.title}</h3>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {m.room_name && <span className="text-xs text-gray-500">🏢 {m.room_name}</span>}
                      {m.host_name && <span className="text-xs text-gray-500">👤 {m.host_name}</span>}
                      {m.duration_mins && <span className="text-xs text-gray-500">⏱ {m.duration_mins} mins</span>}
                    </div>
                    {m.guests && <p className="text-xs text-gray-400 mt-1">Guests: {m.guests}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[m.status]}`}>
                    {m.status}
                  </span>
                  {m.status !== 'cancelled' && (
                    <>
                      <button onClick={() => handleEdit(m)} className="p-1.5 hover:bg-gray-100 rounded">✏️</button>
                      <button onClick={() => handleCancel(m.id)} className="p-1.5 hover:bg-gray-100 rounded text-red-400">🗑️</button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
